import { useCallback } from 'react'

import { useParams } from 'react-router-dom'

import { PaymentMethodEnum } from 'src/Constants/enums'
import { getCookieByName, getUtmParameters } from 'src/Constants/functions'
import { getValueOrEmptyString } from 'src/Helpers/layoutHelpers'
import { getAccessToken } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { sendSourceBusterEvent } from 'src/Redux/sourcebuster-process/api-actions'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { useAppDispatch, useAppSelector } from './redux'
import { useCheckoutEvents } from './useCheckoutEvents'

export const useOrderCheckoutConfirmEvents = (
  platform: CampaignPlatformTypeEnum,
): ((overridePaymentMethod?: PaymentMethodEnum) => void) => {
  const dispatch = useAppDispatch()
  const { number } = useParams()
  const campaignId = Number(number)

  const token = useAppSelector(getAccessToken)
  const paymentMethod = useAppSelector(getPaymentMethod)
  const isRememberPayPalForFuture = useAppSelector(
    getRememberPayPalForFutureFlag,
  )

  const {
    handleSpotifyCheckoutConfirmedPaymentEvent,
    handleTiktokCheckoutConfirmedPaymentEvent,
    sendPaymentDataToWebhook,
  } = useCheckoutEvents()

  return useCallback(
    (overridePaymentMethod?: PaymentMethodEnum): void => {
      const effectivePaymentMethod = overridePaymentMethod ?? paymentMethod
      const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))

      void dispatch(
        sendSourceBusterEvent({
          token,
          campaignId,
          campaignPlatform: platform,
          eventName: 'purchase_started',
          gclid: getValueOrEmptyString(utmObj.gclid),
        }),
      )

      if (platform === CampaignPlatformTypeEnum.TIKTOK) {
        handleTiktokCheckoutConfirmedPaymentEvent(overridePaymentMethod)
      } else {
        handleSpotifyCheckoutConfirmedPaymentEvent(overridePaymentMethod)
      }

      if (effectivePaymentMethod !== PaymentMethodEnum.Credits) {
        const selectedPaymentMethod =
          isRememberPayPalForFuture &&
          effectivePaymentMethod === PaymentMethodEnum.PAYPAL
            ? PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT
            : effectivePaymentMethod
        sendPaymentDataToWebhook({ platform, selectedPaymentMethod })
      }
    },
    [
      campaignId,
      dispatch,
      handleSpotifyCheckoutConfirmedPaymentEvent,
      handleTiktokCheckoutConfirmedPaymentEvent,
      isRememberPayPalForFuture,
      paymentMethod,
      platform,
      sendPaymentDataToWebhook,
      token,
    ],
  )
}
